import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@packages/contexts/ToastContext";
import { Button, Text } from "@core/index";
import { Input } from "@core/input";
import { GenericTable } from "@core/table";
import { getCoreRowModel, useReactTable } from "@tanstack/react-table";
import {
  getGetUserPodcastsQueryKey,
  useAssignPodcastToStudents,
  useGetTeacherAssignedPodcasts,
  getTeacherAssignedPodcasts,
} from "@packages/api/madlenAPI";
import {
  StudentPodcastResponseDtoStatus,
  AssignPodcastRequestDto,
  AssignAssignmentRequestBodyNew,
} from "@packages/api/madlenAPI.schemas";
import Modal from "@core/modal";
import { ClassroomAndStudentSelector } from "@features/assignments/SingleAssignment/componnets/ClassroomAndStudentSelector";
import { format } from "date-fns";
import { tr, enUS } from "date-fns/locale";
interface PodcastAssignmentModalProps {
  podcastId: number;
  podcastTitle?: string;
  show: boolean;
  onClose: () => void;
}

interface SelectedStudentEntry {
  classroomId: number;
  studentIds: number[];
}

// Tarih formatı yardımcı fonksiyonları
const formatDateForInput = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toISOString().slice(0, 16); // "yyyy-MM-ddThh:mm" formatı için
};

const createISOFromInput = (inputValue: string): string => {
  const localDate = new Date(inputValue);

  const timezoneOffset = localDate.getTimezoneOffset();

  const utcDate = new Date(localDate.getTime() - timezoneOffset * 60000);

  return utcDate.toISOString();
};

const usePodcastStudentsTableColumns = () => {
  const { i18n, t } = useTranslation();
  const locale = i18n.language === "tr" ? tr : enUS;
  return [
    {
      accessorKey: "studentName",
      header: t("students.table.name"),
      cell: (info: { getValue: () => string }) => info.getValue(),
    },
    {
      accessorKey: "status",
      header: t("podcast.status.title"),
      cell: (info: { getValue: () => string }) => {
        const status = info.getValue();
        let statusClass = "";
        let statusText = "";

        switch (status) {
          case StudentPodcastResponseDtoStatus.COMPLETED:
            statusClass =
              "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400";
            statusText = t("podcast.status.completed");
            break;
          case StudentPodcastResponseDtoStatus.IN_PROGRESS:
            statusClass =
              "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400";
            statusText = t("podcast.status.inProgress");
            break;
          case StudentPodcastResponseDtoStatus.OVERDUE:
            statusClass =
              "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400";
            statusText = t("podcast.status.overdue");
            break;
          default:
            statusClass =
              "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300";
            statusText = t("podcast.status.pending");
        }

        return (
          <span
            className={`px-2 py-1 rounded-full text-xs font-medium ${statusClass}`}
          >
            {statusText}
          </span>
        );
      },
    },
    {
      accessorKey: "startDate",
      header: t("podcast.startDate"),
      cell: (info: { getValue: () => string }) =>
        format(new Date(info.getValue()), "d MMMM yyyy HH:mm", {
          locale: locale,
        }),
    },
    {
      accessorKey: "endDate",
      header: t("podcast.endDate"),
      cell: (info: { getValue: () => string }) =>
        format(new Date(info.getValue()), "d MMMM yyyy HH:mm", {
          locale: locale,
        }),
    },
    {
      accessorKey: "studentListenedDuration",
      header: t("podcast.listenedDuration"),
      cell: (info: { getValue: () => string }) => {
        const value = info.getValue();
        if (!value) {
          return <span className="text-red-600 dark:text-red-400">-</span>;
        }

        const minutes = Math.floor(parseInt(value) / 60);
        const seconds = parseInt(value) % 60;
        const formattedTime =
          minutes > 0 ? `${minutes}' ${seconds}"` : `${seconds}"`;

        return (
          <span className="text-green-600 dark:text-green-400">
            {formattedTime}
          </span>
        );
      },
    },
  ];
};

const PodcastAssignmentModal = ({
  podcastId,
  podcastTitle,
  show,
  onClose,
}: PodcastAssignmentModalProps) => {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const queryClient = useQueryClient();

  const [showAssignDialog, setShowAssignDialog] = useState<boolean>(false);
  const [selectedStudents, setSelectedStudents] = useState<
    SelectedStudentEntry[]
  >([]);
  const [isAssigning, setIsAssigning] = useState<boolean>(false);

  const currentDateISO = new Date().toISOString();
  const twoWeeksLater = new Date(currentDateISO);
  twoWeeksLater.setDate(twoWeeksLater.getDate() + 14);

  const [endDate, setEndDate] = useState<string>(twoWeeksLater.toISOString());

  const {
    data: assignedStudents = [],
    isLoading,
    error,
  } = useGetTeacherAssignedPodcasts(podcastId, {
    query: {
      queryFn: async () => {
        const response = await getTeacherAssignedPodcasts(podcastId);
        if (response.length === 0) {
          setShowAssignDialog(true);
        }
        return response;
      },
    },
  });

  const table = useReactTable({
    data: assignedStudents,
    columns: usePodcastStudentsTableColumns(),
    getCoreRowModel: getCoreRowModel(),
  });

  const assignedStudentIds = (
    assignedStudents?.map((student) => student.studentId) || []
  ).filter((id): id is number => id !== undefined);

  const getPodcastsQueryKey = getGetUserPodcastsQueryKey();

  const { mutate: assignPodcast } = useAssignPodcastToStudents({
    mutation: {
      onMutate: () => {
        setIsAssigning(true);
      },
      onError: (error) => {
        console.error("Error assigning podcast:", error);
        addToast({
          title: t("notifications.podcast.assignError"),
          variant: "error",
          duration: 2000,
        });
      },
      onSuccess: () => {
        addToast({
          title: t("notifications.podcast.assignSuccess"),
          variant: "success",
          duration: 1000,
        });
        queryClient.invalidateQueries({
          queryKey: getPodcastsQueryKey,
        });
        queryClient.invalidateQueries({
          queryKey: [`/podcasts/students/${podcastId}/assigned`],
        });
      },
      onSettled: () => {
        setIsAssigning(false);
      },
    },
  });

  const handleAssign = () => {
    if (selectedStudents.length === 0) return;

    const assignmentEntries: AssignAssignmentRequestBodyNew[] =
      selectedStudents.map((entry) => ({
        classroomId: entry.classroomId,
        studentIds: entry.studentIds,
      }));

    const payload: AssignPodcastRequestDto = {
      assignedStudentsWithClassrooms: assignmentEntries,
      startDate: new Date().toISOString(),
      endDate: endDate,
    };

    assignPodcast(
      {
        id: podcastId,
        data: payload,
      },
      {
        onSettled: () => {
          setSelectedStudents([]);
          setShowAssignDialog(false);
        },
      },
    );
  };

  return (
    <Modal
      show={show}
      onClose={() => {
        onClose();
        setShowAssignDialog(false);
        setSelectedStudents([]);
      }}
      title={t("podcast.assignmentTitle", { name: podcastTitle })}
      outerClassName="max-w-5xl animate-modal-enter max-h-[90vh] overflow-y-auto"
    >
      <div className="p-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 sm:gap-6 mb-6">
            <Text variant="h3" className="text-xl sm:text-2xl">
              {t("podcast.assignedStudents")}
            </Text>
            <div className="flex gap-2 w-full sm:w-auto">
              {showAssignDialog && (
                <Button
                  size="sm"
                  rounded="lg"
                  bgColor="bg-gray-500 hover:bg-gray-600"
                  textColor="text-white"
                  className="flex items-center gap-2 group flex-1 sm:flex-initial justify-center"
                  onClick={() => setShowAssignDialog(false)}
                  disabled={isAssigning}
                >
                  {t("common.cancel")}
                </Button>
              )}
              <Button
                size="md"
                rounded="lg"
                bgColor="bg-primary-500 hover:bg-primary-600"
                textColor="text-white"
                className="flex items-center gap-2 group flex-1 sm:flex-initial justify-center"
                disabled={
                  (selectedStudents.length === 0 && showAssignDialog) ||
                  isAssigning
                }
                onClick={() => {
                  if (showAssignDialog) {
                    handleAssign();
                  } else {
                    setShowAssignDialog(true);
                  }
                }}
              >
                {!isAssigning && (
                  <svg
                    className="h-4 w-4 transition-transform duration-200 group-hover:scale-110"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                )}
                <span className="whitespace-nowrap">
                  {isAssigning
                    ? t("common.loading")
                    : t("podcast.assignToStudents")}
                </span>
              </Button>
            </div>
          </div>
          <div className="overflow-x-auto -mx-4 sm:mx-0">
            {showAssignDialog ? (
              <div className="min-w-full px-4">
                <div className="mb-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label
                      htmlFor="endDate"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                    >
                      {t("podcast.endDate")}
                    </label>
                    <Input
                      type="datetime-local"
                      value={formatDateForInput(endDate)}
                      min={formatDateForInput(currentDateISO)}
                      onChange={(e) => {
                        setEndDate(createISOFromInput(e.target.value));
                      }}
                      classNames={{
                        input:
                          "w-full p-2 border rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600",
                      }}
                      placeholder={t("podcast.selectEndDate")}
                    />
                  </div>
                </div>

                <ClassroomAndStudentSelector
                  selectedStudents={selectedStudents}
                  setSelectedStudents={setSelectedStudents}
                  assignedStudentIds={assignedStudentIds}
                  shouldUseModal={false}
                />
              </div>
            ) : (
              <div className="min-w-full text-center">
                <GenericTable
                  table={table}
                  isLoading={isLoading}
                  error={error}
                  isEmpty={!assignedStudents?.length}
                  customEmptyState={t("podcast.noAssignedStudents")}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default PodcastAssignmentModal;
